import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { UserStateContext } from 'context/user-state-context';
import { history } from '../../root.component';
import { answerAiCard, createAiThread, getAiAppAccess, getAiThread, getAiThreadMessages, getAiUsageMeter, rateAiMessage, listAiMemories, listAiThreads, uploadAiThreadFile, setAiActiveReply } from 'shared/cinch-ai.api';
import { followCinchAiTurn, getOpenCinchAiTurn, stopCinchAiTurn, streamCinchAiTurn } from './cinch-ai-stream';
import { getActionItems } from 'shared/common.api';
import { OPEN_CUSTOMER_EVENT } from './cinch-ai-events';
import { bubbleSpotAtCorner, bubbleSpotFromSaved, bubbleStorageKey } from './cinch-ai-bubble-spot';
import { DECLINED_MEMORY, declinedRecently, gettingStartedFrom, stepStatusesFrom } from './cinch-ai-getting-started';
import { canSend, readyAttachments } from './cinch-ai-send';
import { threadStarter } from './cinch-ai-thread-start';
import { usageRecheckDelay } from './cinch-ai-usage-reset';
import { CINCH_AI_INTEGRATION, aiVisible, cinchAiAppOn } from './cinch-ai-gate';
import { navigateTarget } from './cinch-ai-navigate';
import { isCinchAiChatPath } from './cinch-ai-paths';
import { allowedPages, describePath, findPage } from './page-registry';
import { questionFor, visibleMessages } from './cinch-ai-siblings';

/**
 * Cinch AI state shared by the bubble window and the Cinch AI page (CINCHAI-108/110).
 *
 * One conversation is "open" at a time; the window and the page render the same one, so
 * opening the page carries the window's chat into it. History lives in backendsafe
 * (`/ai/threads`); this context only holds the open thread and its messages.
 *
 * Window states: `bubble` (closed), `floating`, `docked-left`, `docked-right`. Full screen is
 * not a window state: it is the Cinch AI page route (D17).
 * Future: split this provider (window, conversation, usage) into smaller hooks (Codex review of FE #2325).
 */
export const CinchAiContext = createContext({});
const MAX_PREVIOUS_PAGES = 5;
const PAGE_HISTORY_KEY = 'cinch.ai.pageHistory';
// Kept in step with the proxy's last-80 trim, so a long thread doesn't send history it drops anyway.
const MAX_HISTORY_MESSAGES = 80;
export const DISCLAIMER = 'Cinch AI can make mistakes. Please verify all data, builds, and automated communications before use.';
function readJson(storage, key, fallback) {
  try {
    const value = storage.getItem(key);
    return value ? JSON.parse(value) : fallback;
  } catch (e) {
    return fallback;
  }
}
function writeJson(storage, key, value) {
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch (e) {
    // Private mode or a full quota: the feature works without it.
  }
}
export const CinchAiProvider = ({
  children
}) => {
  const userState = useContext(UserStateContext);
  const {
    token,
    asCompany,
    user,
    flags,
    hasPermission,
    hasIntegration,
    isCatalystUser,
    isPLG,
    companies
  } = userState;
  const location = useLocation();
  const userKey = user?.id ? `cinch.ai.${user.id}` : null;
  // The working-as company now. A meter, recents or Getting Started read started for the company the
  // user just left checks this when it answers, and drops out (Codex review of FE #2325).
  const companyIdRef = useRef(asCompany?.id);
  companyIdRef.current = asCompany?.id;

  // --- window ------------------------------------------------------------------
  const [windowState, setWindowStateRaw] = useState('bubble');
  const [bubbleHidden, setBubbleHidden] = useState(false);
  const [unread, setUnread] = useState(false);
  const windowStateRef = useRef(windowState);
  useEffect(() => {
    if (!userKey) return;
    setBubbleHidden(readJson(localStorage, `${userKey}.bubbleHidden`, false));
  }, [userKey]);

  // The bubble's spot (Justin, 2026-10-29): the user's own {left, bottom}, or null for the default spot
  // beside the nav. Shared so the window opens from the bubble and the bubble comes back where the window
  // was. Saved per user with the bubble's hint state (`${userKey}.bubble`).
  const [bubblePos, setBubblePosRaw] = useState(null);
  const panelElRef = useRef(null); // the floating or docked window's element, while it shows
  const bubbleRectRef = useRef(null); // where the bubble was when it opened the window
  useEffect(() => {
    if (!userKey) return;
    const saved = readJson(localStorage, bubbleStorageKey(userKey), null);
    setBubblePosRaw(bubbleSpotFromSaved(saved?.pos));
  }, [userKey]);
  const setBubblePos = useCallback(pos => {
    setBubblePosRaw(pos);
    if (userKey) writeJson(localStorage, bubbleStorageKey(userKey), {
      ...readJson(localStorage, bubbleStorageKey(userKey), {}),
      pos
    });
  }, [userKey]);
  // The floating window's last spot ({left, bottom}; Justin, 2026-10-30): when the agent opens an app page
  // from Full screen, the panel comes back as the window, where it last was. Saved per user
  // (`${userKey}.window`). `restoreWindowSpotRef` asks the panel to open there instead of at the bubble.
  const windowSpotRef = useRef(null);
  const restoreWindowSpotRef = useRef(false);
  useEffect(() => {
    windowSpotRef.current = userKey ? readJson(localStorage, `${userKey}.window`, null) : null;
  }, [userKey]);
  const rememberWindowSpot = useCallback(spot => {
    windowSpotRef.current = spot;
    if (userKey && spot) writeJson(localStorage, `${userKey}.window`, spot);
  }, [userKey]);
  const setWindowState = useCallback(next => {
    // Shrinking the floating window to the bubble (minimize, or the agent opening a page or a drawer):
    // the bubble appears at the window's nearest corner, not at its default spot. The user can drag it back.
    // Only the floating window: a docked one sits against the screen edge (docked right, its corner
    // would put the bubble back under the customer drawer).
    if (next === 'bubble' && windowStateRef.current === 'floating' && panelElRef.current) {
      setBubblePos(bubbleSpotAtCorner(panelElRef.current.getBoundingClientRect()));
    }
    windowStateRef.current = next;
    setWindowStateRaw(next);
    if (next !== 'bubble') setUnread(false);
  }, [setBubblePos]);
  const toggleBubbleHidden = useCallback(() => {
    setBubbleHidden(hidden => {
      if (userKey) writeJson(localStorage, `${userKey}.bubbleHidden`, !hidden);
      return !hidden;
    });
  }, [userKey]);

  // --- page context ------------------------------------------------------------
  // Current page plus the last five, with when each was opened, so the agent can resolve
  // "the one I was just looking at" (Precious Relics pattern, discovery §3.7).
  const [pageHistory, setPageHistory] = useState(() => readJson(sessionStorage, PAGE_HISTORY_KEY, []));
  const [shareContext, setShareContext] = useState(true);
  useEffect(() => {
    const path = location.pathname + location.search;
    setPageHistory(prev => {
      if (prev[0]?.path === path) return prev;
      const next = [{
        path,
        at: Date.now()
      }, ...prev.filter(p => p.path !== path)].slice(0, MAX_PREVIOUS_PAGES + 1);
      writeJson(sessionStorage, PAGE_HISTORY_KEY, next);
      return next;
    });
  }, [location.pathname, location.search]);

  // The Cinch AI app (CINCHAI-122): installed on the company, and this user on its allow-list.
  // Unknown (null) while loading, so nothing Cinch AI shows until the answer is in.
  // Dark on merge (Chris's review of FE #2325): with the rollout flag off nothing here calls `/ai/*`, so
  // the check waits for the flag, and runs when it turns on later in the session (flags reload per company).
  const flagOn = cinchAiAppOn(flags);
  const [aiAccess, setAiAccess] = useState(null);
  useEffect(() => {
    if (!flagOn || !asCompany?.id || !token) {
      setAiAccess(null);
      return undefined;
    }
    let cancelled = false;
    setAiAccess(null);
    getAiAppAccess(asCompany.id, token).then(access => !cancelled && setAiAccess(access)).catch(e => {
      console.error('Cinch AI: checking app access failed', e);
      return !cancelled && setAiAccess({
        installed: false,
        allowed: false
      });
    });
    return () => {
      cancelled = true;
    };
  }, [flagOn, asCompany?.id, token]);
  // The one gate for everything native Cinch AI shows (CINCHAI-122, Justin 2026-10-29): the
  // LaunchDarkly rollout flag `cinch-ai-app`, the Cinch AI app installed on the company (like Cinch
  // Phones' `hasIntegration('cinch_phones')`), and this user on its allow-list. The flags
  // `ai_chat_bot` / `cinch_ai` are left only to the legacy socket widget (CINCHAI-127 removes them).
  // Switching to a company without the app hides everything at once (`aiAccess` resets above).
  // Internal users (superusers and @cinch.io; Justin, 2026-10-30) see it on any company they can access,
  // installed or not (cinch-ai-gate.js).
  const aiAllowed = aiVisible(flagOn, hasIntegration(CINCH_AI_INTEGRATION), aiAccess);
  const predicateContext = useMemo(() => ({
    hasPermission,
    hasIntegration,
    isCatalystUser,
    flags: flags || {},
    user,
    hasChildrenCompanies: false,
    isPLG,
    cinchAiAllowed: aiAllowed
  }), [hasPermission, hasIntegration, isCatalystUser, flags, user, isPLG, aiAllowed]);
  const buildPageContext = useCallback(() => {
    if (!shareContext) return null;
    const now = Date.now();
    const [current, ...previous] = pageHistory;
    const describe = entry => ({
      path: entry.path,
      label: describePath(entry.path),
      seconds_ago: Math.round((now - entry.at) / 1000)
    });
    return current ? {
      current: describe(current),
      previous: previous.map(describe)
    } : null;
  }, [pageHistory, shareContext]);

  // --- threads -----------------------------------------------------------------
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isStreaming, setIsStreaming] = useState(false);
  // A chat is loading (openThread): until its thread and messages are both in, the screen can show the
  // previous chat's, so nothing is sent (the composer waits) and no history goes to the wrong thread.
  const [threadLoading, setThreadLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [recents, setRecents] = useState([]);
  const [recentsLoading, setRecentsLoading] = useState(false);
  // Scope for the NEXT new thread (the page's scope chip, CINCHAI-110). Fixed once a thread exists.
  const [newThreadScope, setNewThreadScope] = useState('company');
  // Folder for the NEXT new thread ("New chat in this folder", CINCHAI-118); its prompt applies.
  const [newThreadFolder, setNewThreadFolder] = useState(null);
  // The user's usage and the company's caps (CINCHAI-121), and a limit that refused the last send.
  const [usage, setUsage] = useState(null);
  const [usageRefused, setUsageRefused] = useState(null);
  const refreshUsage = useCallback(async () => {
    // Waits for the token: on page load the company is known before the API client is signed in.
    // And for access: nobody sees the meter without it, and with the flag off nothing calls `/ai/*`.
    // Until then no meter: the last one may be the company the user just left (access re-checks on a switch).
    if (!aiAllowed || !asCompany?.id || !token) {
      setUsage(null);
      return;
    }
    const company = asCompany.id;
    try {
      const meter = await getAiUsageMeter(company, token);
      if (companyIdRef.current === company) setUsage(meter);
    } catch (e) {
      console.error('Cinch AI: loading the usage meter failed', e);
      if (companyIdRef.current === company) setUsage(null); // no meter is better than a wrong one
    }
  }, [aiAllowed, asCompany?.id, token]);
  useEffect(() => {
    setUsageRefused(null);
    refreshUsage();
  }, [refreshUsage]);
  // A paused composer unlocks when its window resets, without a reload (Codex review of FE #2325): just
  // after `resets_when` the refusal clears and the meter is read again; still capped, it stays paused.
  useEffect(() => {
    const delay = usageRecheckDelay(usage?.limits, usageRefused);
    if (delay === null) return undefined;
    const timer = setTimeout(() => {
      setUsageRefused(null);
      refreshUsage();
    }, delay);
    return () => clearTimeout(timer);
  }, [usage, usageRefused, refreshUsage]);
  // Suggestion priority, Tier 1 (CINCHAI-135): the company's Getting Started steps, read when the chat
  // opens and after each turn (the agent may have finished one), sent with every turn so the agent
  // offers the next step first; `declined` while the user's "not now" memory is under 7 days old.
  const [gettingStarted, setGettingStarted] = useState(null);
  // Every step's status, for the empty chat's suggestions (CINCHAI-146); null when unknown.
  const [stepStatuses, setStepStatuses] = useState(null);
  const refreshGettingStarted = useCallback(async () => {
    if (!aiAllowed || !asCompany?.id) {
      setGettingStarted(null);
      setStepStatuses(null);
      return;
    }
    const company = asCompany.id;
    try {
      const [{
        data
      }, declined] = await Promise.all([getActionItems({
        company
      }), listAiMemories({
        scope: 'personal',
        name: DECLINED_MEMORY,
        limit: 1
      }).then(page => (page?.results || [])[0]).catch(e => {
        console.error('Cinch AI: loading the declined Getting Started memory failed', e);
        return null;
      })]);
      if (companyIdRef.current !== company) return; // the user switched companies meanwhile
      setGettingStarted({
        ...gettingStartedFrom(data),
        declined: declinedRecently(declined)
      });
      setStepStatuses(stepStatusesFrom(data));
    } catch (e) {
      console.error('Cinch AI: loading Getting Started steps failed', e);
      if (companyIdRef.current !== company) return;
      setGettingStarted(null); // no offer is better than a wrong one
      setStepStatuses(null);
    }
  }, [aiAllowed, asCompany?.id]);
  useEffect(() => {
    refreshGettingStarted();
  }, [refreshGettingStarted]);
  // Names of the memories the proxy loaded into the last turn (CINCHAI-112 composer chip).
  const [loadedMemories, setLoadedMemories] = useState([]);
  const abortRef = useRef(null);
  // The running turn's server-side job id (CINCHAI-119): Stop cancels it on the proxy.
  const turnRef = useRef(null);
  // Which chat is on screen: every open and new chat bumps it (Chris's review of FE #2325). A load,
  // reattach or stream started for an earlier one sees the change and drops out, so nothing from the
  // chat the user left lands in the one they opened.
  const openSeqRef = useRef(0);
  // Files dropped or pasted into the composer, waiting for the next message (CINCHAI-129):
  // [{ key, name, status: 'uploading' | 'ready' | 'failed', id?, error? }].
  const [attachments, setAttachments] = useState([]);
  const refreshRecents = useCallback(async () => {
    if (!asCompany?.id) return;
    const company = asCompany.id;
    setRecentsLoading(true);
    try {
      const data = await listAiThreads({
        company,
        limit: 20
      });
      if (companyIdRef.current === company) setRecents(data?.results || []);
    } catch (e) {
      console.error('Cinch AI: loading recent chats failed', e);
      if (companyIdRef.current === company) setRecents([]);
    } finally {
      setRecentsLoading(false);
    }
  }, [asCompany?.id]);

  // A thread is needed before a file can be stored, so the first drop starts the thread; the first
  // message waits for the same one (cinch-ai-thread-start.js). Resolves null when the user started
  // another chat, or opened one, while it was being created: that thread is never shown.
  const startThreadRef = useRef(null);
  if (!startThreadRef.current) startThreadRef.current = threadStarter(() => openSeqRef.current);
  const ensureThread = useCallback(async () => {
    if (thread) return thread;
    const created = await startThreadRef.current(openSeqRef.current, () => createAiThread({
      company: asCompany.id,
      scope: newThreadScope,
      folder: newThreadFolder
    }));
    if (created) setThread(created);
    return created;
  }, [thread, asCompany?.id, newThreadScope, newThreadFolder]);
  const addFiles = useCallback(async fileList => {
    const files = Array.from(fileList || []);
    // Not while a chat loads: the thread on hand may still be the one the user is leaving.
    if (!files.length || !asCompany?.id || threadLoading) return;
    const entries = files.map(file => ({
      key: `${file.name}-${file.size}-${Math.random()}`,
      name: file.name,
      status: 'uploading'
    }));
    setAttachments(prev => [...prev, ...entries]);
    let target;
    try {
      target = await ensureThread();
    } catch (e) {
      console.error('Cinch AI: starting a chat for a file failed', e);
      setAttachments(prev => prev.map(a => entries.some(n => n.key === a.key) ? {
        ...a,
        status: 'failed',
        error: 'No chat'
      } : a));
      return;
    }
    if (!target) return; // the user left this chat while it started; its files went with it
    await Promise.all(files.map(async (file, i) => {
      const key = entries[i].key;
      try {
        const saved = await uploadAiThreadFile(target.id, file);
        setAttachments(prev => prev.map(a => a.key === key ? {
          ...a,
          status: 'ready',
          id: saved.id
        } : a));
      } catch (e) {
        console.error('Cinch AI: uploading a file failed', e);
        const error = e?.response?.data?.file?.[0] || 'Upload failed';
        setAttachments(prev => prev.map(a => a.key === key ? {
          ...a,
          status: 'failed',
          error
        } : a));
      }
    }));
  }, [asCompany?.id, threadLoading, ensureThread]);
  const removeAttachment = useCallback(key => setAttachments(prev => prev.filter(a => a.key !== key)), []);
  const newChat = useCallback(() => {
    openSeqRef.current += 1;
    abortRef.current?.abort();
    setAttachments([]);
    setLoadedMemories([]);
    setThreadLoading(false);
    setThread(null);
    setMessages([]);
    setError(null);
    setNewThreadScope('company');
    setNewThreadFolder(null);
  }, []);

  // Product decision: switching working-as starts a new chat in the window; the old thread
  // stays in history under its own company (a thread never changes company, D14). Except a chat that
  // belongs to the company switched to ("Switch to <company>" on its banner): that is the chat the user
  // switched for, so it stays open. On the Cinch AI page the URL names the open chat; it moves to the new
  // chat too, or the page (it remounts while access is re-checked) would reopen the old company's chat.
  const threadRef = useRef(thread);
  threadRef.current = thread;
  const companyRef = useRef(asCompany?.id);
  useEffect(() => {
    const before = companyRef.current;
    companyRef.current = asCompany?.id;
    if (!before || !asCompany?.id || before === asCompany.id) return;
    const open = threadRef.current;
    if (open?.scope === 'company' && open.company === asCompany.id) return;
    newChat();
    if (open?.id && history.location.pathname === `/cinch-ai/${open.id}`) history.replace(`/cinch-ai${history.location.search}`);
  }, [asCompany?.id, newChat]);
  const openThread = useCallback(async threadOrId => {
    openSeqRef.current += 1;
    const seq = openSeqRef.current;
    const stillOpen = () => seq === openSeqRef.current;
    abortRef.current?.abort();
    const id = typeof threadOrId === 'string' ? threadOrId : threadOrId.id;
    setError(null);
    // Files wait for the chat they were dropped in (their uploads are stored on its thread), and the
    // memories chip is the last turn's: neither carries over to another chat.
    setAttachments([]);
    setLoadedMemories([]);
    if (typeof threadOrId === 'object') setThread(threadOrId);
    setThreadLoading(true);
    try {
      const loaded = await getAiThreadMessages(id);
      if (!stillOpen()) return; // the user opened another chat meanwhile
      setMessages(loaded || []);
      if (typeof threadOrId === 'string') {
        const opened = await getAiThread(id);
        if (!stillOpen()) return;
        setThread(opened);
      }
    } catch (e) {
      console.error('Cinch AI: opening a chat failed', e);
      if (stillOpen()) setError('That chat could not be opened.');
      return;
    } finally {
      if (stillOpen()) setThreadLoading(false); // otherwise the chat opened since owns the flag
    }
    reattachRef.current?.(id, seq);
  }, []);
  // openThread is defined before reattach (which needs consumeTurn); the ref bridges the two.
  const reattachRef = useRef(null);

  // --- navigation by the agent (CINCHAI-109) ------------------------------------
  // Opening an app page from Cinch AI: a navigate action, or an in-app link in a reply (Justin, 2026-10-30).
  const openAppPath = useCallback(path => {
    if (isCinchAiChatPath(location.pathname)) {
      // Full screen (the Cinch AI page; Justin, 2026-10-30): opening an app page drops the panel to the
      // window, where it last was (or the default spot), so the user sees the conversation and the page.
      restoreWindowSpotRef.current = true;
      history.push(path);
      setWindowState('floating');
      return;
    }
    // R8: the floating window shrinks to the bubble, then the page changes. Docked stays docked
    // (Justin, 2026-10-30): the page changes beside it.
    if (windowStateRef.current === 'floating') setWindowState('bubble');
    history.push(path);
  }, [location.pathname, setWindowState]);

  // Like realtime-context's ACTIONS map: the proxy can only name a kind we already handle.
  const runAction = useCallback(action => {
    if (action?.kind === 'open_customer') {
      // CINCHAI-142: open a customer's drawer. Re-check the permissions here (the agent's page list
      // could be stale). A draft only fills the Messaging box; the user sends it, never Cinch AI.
      if (!hasPermission('customer.view_customer')) return {
        ok: false,
        reason: "You don't have access to customers."
      };
      if (action.tab === 'messaging' && !hasPermission('customer.view_two_way_messaging')) {
        return {
          ok: false,
          reason: "You don't have access to a customer's messages."
        };
      }
      if (action.draft && !hasPermission('customer.send_two_way_messaging')) {
        return {
          ok: false,
          reason: "You can't send messages to customers, so there's no draft to review."
        };
      }
      if (windowStateRef.current !== 'bubble' && !isCinchAiChatPath(location.pathname)) setWindowState('bubble');
      window.dispatchEvent(new CustomEvent(OPEN_CUSTOMER_EVENT, {
        detail: action
      }));
      return {
        ok: true
      };
    }
    if (action?.kind !== 'navigate') return {
      ok: false
    };
    // Re-check on the client: the allowed list could be stale (permissions changed mid-chat). The
    // check and the route read the same field (cinch-ai-navigate.js).
    const target = navigateTarget(action, findPage, predicateContext);
    if (target.reason) return {
      ok: false,
      reason: target.reason
    };
    openAppPath(target.path);
    return {
      ok: true
    };
  }, [predicateContext, location.pathname, setWindowState, hasPermission, openAppPath]);

  // --- one turn's stream, live or reattached (CINCHAI-119) ---------------------------
  // `replay` = a reattach after a reload: navigate actions are recorded, never run again, and
  // the user's message (not saved until the turn ends) comes from the `turn` event.
  const consumeTurn = useCallback(async ({
    start,
    userLocalId,
    replyId,
    replay = false
  }) => {
    // The chat this turn belongs to: once the user opens another one (or a new chat), nothing from
    // this stream touches the screen, not its text, events, "Stopped" or errors.
    const seq = openSeqRef.current;
    const isCurrent = () => seq === openSeqRef.current;
    setIsStreaming(true);
    setUsageRefused(null); // a new attempt; the meter says where things stand now
    const updateReply = fn => {
      if (isCurrent()) setMessages(prev => prev.map(m => m.localId === replyId ? fn(m) : m));
    };
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      await start({
        signal: controller.signal,
        onText: delta => updateReply(m => ({
          ...m,
          content_md: m.content_md + delta
        })),
        onEvent: event => {
          if (!isCurrent()) return;
          if (event.type === 'turn') {
            turnRef.current = event.turn_id;
            if (replay && event.user_message) {
              setMessages(prev => {
                const at = prev.findIndex(m => m.localId === replyId);
                const question = {
                  role: 'user',
                  content_md: event.user_message,
                  localId: userLocalId
                };
                return at < 0 ? prev : [...prev.slice(0, at), question, ...prev.slice(at)];
              });
            }
          } else if (event.type === 'thread' && event.thread) {
            setThread(t => t && t.id === event.thread.id ? {
              ...t,
              ...event.thread
            } : t);
            if (event.thread.title) refreshRecents();
          } else if (event.type === 'saved') {
            // Give the local copies their server ids and seq (sharing and regenerate key on seq).
            const byRole = Object.fromEntries((event.messages || []).map(m => [m.role, m]));
            setMessages(prev => prev.map(m => {
              if (m.localId === userLocalId && byRole.user) return {
                ...m,
                id: byRole.user.id,
                seq: byRole.user.seq
              };
              if (m.localId === replyId && byRole.assistant) return {
                ...m,
                id: byRole.assistant.id,
                seq: byRole.assistant.seq
              };
              return m;
            }));
          } else if (event.type === 'memories') {
            setLoadedMemories(event.names || []);
          } else if (event.type === 'tasks') {
            updateReply(m => ({
              ...m,
              tasks: event.tasks || []
            }));
          } else if (event.type === 'error') {
            // Design review 2, item 14: the reply shows a plain line; the raw error only behind Details.
            updateReply(m => ({
              ...m,
              turnError: event.detail || ''
            }));
          } else if (event.type === 'tools') {
            // Tool lines resolve to ✓/✕ and a short result as each round finishes (design review).
            updateReply(m => ({
              ...m,
              tools: [...(m.tools || []), ...(event.tools || [])]
            }));
          } else if (event.type === 'navigate' && event.action) {
            const result = replay ? {
              ok: true
            } : runAction(event.action);
            updateReply(m => ({
              ...m,
              actions: [...(m.actions || []), {
                ...event.action,
                blocked: !result.ok
              }]
            }));
          } else if (event.type === 'action' && event.action) {
            // Shown, never run (CINCHAI-123): created-item cards and the segment draft.
            updateReply(m => ({
              ...m,
              actions: [...(m.actions || []), event.action]
            }));
          }
        }
      });
    } catch (e) {
      if (!isCurrent()) {
        // The user opened another chat (the turn keeps running on the server); its end isn't shown there.
        if (e?.name !== 'AbortError') console.error('Cinch AI: a reply in a chat the user left failed', e);
      } else if (e?.name === 'AbortError') {
        // Stop.
        updateReply(m => ({
          ...m,
          content_md: `${m.content_md}\n\n_Stopped._`
        }));
      } else if (e?.code === 'cinch_session_expired') {
        setSessionExpired(true);
        updateReply(m => ({
          ...m,
          failed: true
        }));
      } else if (e?.code === 'cinch_usage_limit') {
        // Refused before any model call, so nothing was sent or saved: drop the question and the
        // empty reply; the banner says why and until when.
        setUsageRefused(e.details);
        setMessages(prev => prev.filter(m => m.localId !== replyId && m.localId !== userLocalId));
      } else if (replay && e?.status === 404) {
        // The turn ended between the check and the reattach; the saved reply loads on reopen.
        setMessages(prev => prev.filter(m => m.localId !== replyId));
      } else {
        console.error('Cinch AI: streaming a reply failed', e);
        setError(e?.message || 'Cinch AI is unavailable right now.');
        updateReply(m => ({
          ...m,
          failed: true
        }));
      }
    } finally {
      updateReply(m => ({
        ...m,
        pending: false
      }));
      // A newer turn (the reattach of the chat the user opened) may own the stream state by now.
      if (abortRef.current === controller) {
        setIsStreaming(false);
        abortRef.current = null;
        turnRef.current = null;
      }
      if (isCurrent() && windowStateRef.current === 'bubble') setUnread(true);
      refreshRecents();
      refreshUsage();
      refreshGettingStarted();
    }
  }, [refreshRecents, runAction, refreshUsage, refreshGettingStarted]);

  // --- sending -----------------------------------------------------------------
  const sendMessage = useCallback(async typed => {
    // A message is text, ready files, or both (Justin, 2026-10-30); nothing at all isn't sent.
    const text = typed || '';
    const sending = readyAttachments(attachments);
    if (!canSend(text, attachments) || isStreaming || threadLoading || !asCompany?.id) return;
    setError(null);
    setSessionExpired(false);

    // Files still uploading (or failed) stay in the composer for the next message; the
    // composer has already cleared the text, so this message must go now.
    // A new chat's thread: the one a file drop is already starting, if any (ensureThread).
    let current;
    try {
      current = await ensureThread();
    } catch (e) {
      console.error('Cinch AI: starting a chat failed', e);
      setError(e?.response?.data?.scope?.[0] || e?.response?.data?.company?.[0] || 'Cinch AI could not start a chat.');
      return;
    }
    // Known gap: the user left this new chat (New chat, another chat, a company switch) while its
    // thread was being created. The message is dropped rather than shown in the chat now open, and the
    // empty thread stays behind like any chat started and never used.
    if (!current) return;
    const userMessage = {
      role: 'user',
      content_md: text,
      localId: `u-${Date.now()}`,
      files: sending.map(a => ({
        id: a.id,
        name: a.name
      }))
    };
    setAttachments(prev => prev.filter(a => a.status !== 'ready'));
    const replyId = `a-${Date.now()}`;
    // Only the replies shown continue the conversation (CINCHAI-124: not the other versions).
    const history_ = [...visibleMessages(messages), userMessage];
    setMessages([...messages, userMessage, {
      role: 'assistant',
      content_md: '',
      actions: [],
      localId: replyId,
      pending: true
    }]);
    await consumeTurn({
      userLocalId: userMessage.localId,
      replyId,
      start: ({
        signal,
        onText,
        onEvent
      }) => streamCinchAiTurn({
        token,
        signal,
        onText,
        onEvent,
        body: {
          thread_id: current.id,
          cinch_company_id: current.scope === 'all_companies' ? undefined : current.company,
          cinch_company_name: current.company_name,
          page_context: buildPageContext(),
          allowed_pages: allowedPages(predicateContext),
          getting_started: gettingStarted || undefined,
          file_ids: sending.map(a => a.id),
          messages: history_.slice(-MAX_HISTORY_MESSAGES).map(m => ({
            role: m.role,
            content: m.content_md || ''
          }))
        }
      })
    });
  }, [isStreaming, threadLoading, asCompany?.id, ensureThread, messages, token, buildPageContext, predicateContext, attachments, consumeTurn, gettingStarted]);

  // Regenerate the last reply (CINCHAI-124): the same question again, saved as a sibling of the
  // shown reply; the new one shows and later turns continue from it.
  const regenerate = useCallback(async reply => {
    if (isStreaming || threadLoading || !thread?.id || !reply?.id || !asCompany?.id) return;
    setError(null);
    const shown = visibleMessages(messages);
    const question = questionFor(shown, reply);
    if (!question?.id) return;
    const replyId = `a-${Date.now()}`;
    setMessages(prev => [...prev.map(m => {
      if (m.id === reply.id && !m.parent) return {
        ...m,
        parent: question.id
      };
      if (m.id === question.id) return {
        ...m,
        active_child: null
      };
      return m;
    }), {
      role: 'assistant',
      content_md: '',
      actions: [],
      localId: replyId,
      pending: true,
      parent: question.id
    }]);
    const history_ = shown.slice(0, shown.indexOf(question) + 1);
    await consumeTurn({
      userLocalId: null,
      replyId,
      start: ({
        signal,
        onText,
        onEvent
      }) => streamCinchAiTurn({
        token,
        signal,
        onText,
        onEvent,
        body: {
          thread_id: thread.id,
          cinch_company_id: thread.scope === 'all_companies' ? undefined : thread.company,
          cinch_company_name: thread.company_name,
          page_context: buildPageContext(),
          allowed_pages: allowedPages(predicateContext),
          getting_started: gettingStarted || undefined,
          regenerate_parent: question.id,
          messages: history_.slice(-MAX_HISTORY_MESSAGES).map(m => ({
            role: m.role,
            content: m.content_md || ''
          }))
        }
      })
    });
  }, [isStreaming, threadLoading, thread, asCompany?.id, messages, token, buildPageContext, predicateContext, consumeTurn, gettingStarted]);

  // The "‹ 1/2 ›" switcher: show another version; it is kept, and later turns continue from it.
  const selectReply = useCallback(async (question, childId) => {
    if (!thread?.id || !question?.id || !childId) return;
    const before = question.active_child;
    setMessages(prev => prev.map(m => m.id === question.id ? {
      ...m,
      active_child: childId
    } : m));
    try {
      await setAiActiveReply(thread.id, question.id, childId);
    } catch (e) {
      console.error('Cinch AI: switching the shown reply failed', e);
      setMessages(prev => prev.map(m => m.id === question.id ? {
        ...m,
        active_child: before
      } : m));
    }
  }, [thread?.id]);

  // Re-read the open thread after a rename / pin / move from a header, without reopening it.
  const refreshThread = useCallback(async () => {
    if (!thread?.id) return;
    const seq = openSeqRef.current;
    try {
      const fresh = await getAiThread(thread.id);
      if (seq === openSeqRef.current) setThread(fresh); // still the chat on screen
    } catch (e) {
      console.error('Cinch AI: refreshing the chat failed', e); // keep what is shown
    }
  }, [thread?.id]);

  // Thumbs on a reply (design review, CINCHAI-108); the same value again clears it.
  const rateMessage = useCallback(async (message, value) => {
    if (!thread?.id || !message.id) return;
    const next = message.feedback === value ? '' : value;
    setMessages(prev => prev.map(m => m.id === message.id ? {
      ...m,
      feedback: next
    } : m));
    try {
      await rateAiMessage(message.id, next);
    } catch (e) {
      console.error('Cinch AI: rating a reply failed', e);
      setMessages(prev => prev.map(m => m.id === message.id ? {
        ...m,
        feedback: message.feedback
      } : m));
    }
  }, [thread?.id]);

  // Answer a question card (design review 2, final pass B): the answer is recorded on the reply
  // first, so the card stays answered after a reload and a second click never sends it again.
  const answerCard = useCallback(async (reply, card, text) => {
    if (!thread?.id || !reply.id) {
      sendMessage(text);
      return;
    }
    const markAnswered = answer => setMessages(prev => prev.map(m => m.id === reply.id ? {
      ...m,
      actions: [...(m.actions || []), {
        kind: 'answered',
        card,
        answer
      }]
    } : m));
    try {
      await answerAiCard(thread.id, reply.id, card, text);
    } catch (e) {
      if (e?.response?.status === 409) {
        // Answered already (another tab, or before a reload): show that answer, send nothing.
        markAnswered(e.response.data?.answer || text);
        return;
      }
      console.error('Cinch AI: saving a question card answer failed', e);
      // Known gap: if the marker can't be saved the answer still goes; the memory write path
      // refuses the same fact twice as a backstop.
    }
    markAnswered(text);
    sendMessage(text);
  }, [thread?.id, sendMessage]);

  // Stop cancels the turn on the proxy (it saves the partial reply), then drops the stream.
  const stop = useCallback(() => {
    if (turnRef.current) {
      stopCinchAiTurn({
        token,
        turnId: turnRef.current
      }).catch(e => console.error('Cinch AI: stopping the turn on the proxy failed', e));
    }
    abortRef.current?.abort();
  }, [token]);

  // A chat opened while its turn is still running on the server (a reload mid-build, another
  // tab, the phone) reattaches and watches the same turn finish (CINCHAI-119).
  // `seq` is openThread's: if the user opened another chat while the check was out, nothing is attached.
  const reattach = useCallback(async (threadId, seq) => {
    let open = null;
    try {
      open = await getOpenCinchAiTurn({
        token,
        threadId
      });
    } catch (e) {
      console.error('Cinch AI: checking for a running turn failed', e);
      return; // the proxy is down; the saved history is all there is
    }
    if (!open?.turn_id || seq !== openSeqRef.current) return;
    const replyId = `a-${Date.now()}`;
    setMessages(prev => [...prev, {
      role: 'assistant',
      content_md: '',
      actions: [],
      localId: replyId,
      pending: true
    }]);
    await consumeTurn({
      userLocalId: `u-${Date.now()}`,
      replyId,
      replay: true,
      start: ({
        signal,
        onText,
        onEvent
      }) => followCinchAiTurn({
        token,
        turnId: open.turn_id,
        signal,
        onText,
        onEvent
      })
    });
  }, [token, consumeTurn]);
  reattachRef.current = reattach;
  const value = {
    // window
    windowState,
    setWindowState,
    bubblePos,
    setBubblePos,
    panelElRef,
    bubbleRectRef,
    windowSpotRef,
    restoreWindowSpotRef,
    rememberWindowSpot,
    bubbleHidden,
    toggleBubbleHidden,
    unread,
    // conversation
    thread,
    messages,
    isStreaming,
    threadLoading,
    error,
    sessionExpired,
    sendMessage,
    answerCard,
    stop,
    newChat,
    openThread,
    refreshThread,
    newThreadScope,
    setNewThreadScope,
    newThreadFolder,
    setNewThreadFolder,
    loadedMemories,
    rateMessage,
    regenerate,
    selectReply,
    aiAccess,
    aiAllowed,
    gettingStarted,
    stepStatuses,
    usage,
    usageRefused,
    attachments,
    addFiles,
    removeAttachment,
    recents,
    recentsLoading,
    refreshRecents,
    // context
    pageHistory,
    shareContext,
    setShareContext,
    buildPageContext,
    runAction,
    openAppPath,
    asCompany,
    companies,
    userKey
  };
  return <CinchAiContext.Provider value={value}>{children}</CinchAiContext.Provider>;
};
export const useCinchAi = () => useContext(CinchAiContext);